import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ClipboardList, Search, X } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { useSelector } from 'react-redux';
import { selectCompanyName } from '@/store/slices/companySlice';
import { selectActiveCompanyId } from '@/store/slices/workspaceSlice';
import { useDailyAttendance } from '@/api/queries/attendance.queries';
import { useDepartments } from '@/api/queries/employee.queries';
import { StatCard } from '@/components/shared/StatCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ReportExportBar } from '@/components/reports/ReportExportBar';
import { buildDetailRows } from '@/lib/ReportGenerator';
import { cn } from '@/lib/cn';

const STATUS_OPTS = ['PRESENT', 'ABSENT', 'LATE', 'HALF_DAY', 'ON_LEAVE', 'HOLIDAY', 'WEEKLY_OFF'];

export default function AttendanceReportPage() {
  const companyName     = useSelector(selectCompanyName);
  const activeCompanyId = useSelector(selectActiveCompanyId);
  const today = format(new Date(), 'yyyy-MM-dd');
  const [date, setDate]         = useState(today);
  const [search, setSearch]     = useState('');
  const [deptFilter, setDept]   = useState('');
  const [statusFilter, setStatus] = useState('');

  const params = useMemo(() => ({
    date,
    ...(search ? { search } : {}),
    ...(deptFilter ? { department_id: deptFilter } : {}),
    ...(statusFilter ? { status: statusFilter } : {}),
    limit: 500,
  }), [date, search, deptFilter, statusFilter]);

  const { data: attData, isLoading } = useDailyAttendance(params, activeCompanyId);
  const { data: deptData }           = useDepartments();

  const records     = attData?.data || [];
  const departments = deptData?.data || [];

  const kpi = useMemo(() => {
    const counts = {};
    records.forEach(r => { counts[r.status] = (counts[r.status] || 0) + 1; });
    return counts;
  }, [records]);

  // Active status filter badges
  const statusCounts = useMemo(() => {
    const all = attData?.data || [];
    const m = {};
    all.forEach(r => { m[r.status] = (m[r.status] || 0) + 1; });
    return m;
  }, [attData]);

  const exportRows = useMemo(() => buildDetailRows(records), [records]);
  const filterSummary = `${date}${deptFilter ? ` · ${departments.find(d => d.id === deptFilter)?.name}` : ''}${statusFilter ? ` · ${statusFilter}` : ''}`;

  return (
    <div className="p-6 space-y-5">
      <nav className="flex items-center gap-1.5 text-[12px] text-[var(--text-tertiary)]">
        <Link to="/reports" className="hover:text-[var(--accent)]">Reports</Link>
        <ChevronRight size={12}/>
        <span className="text-[var(--text-primary)] font-medium">Attendance</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">Attendance Report</h1>
          <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">{date} · {companyName}</p>
        </div>
        <ReportExportBar rows={exportRows} filename={`attendance-${date}`} title="Attendance Report" companyName={companyName} filterSummary={filterSummary}/>
      </div>

      {/* KPI status pills */}
      <div className="flex flex-wrap gap-2">
        {STATUS_OPTS.map(s => (
          <button key={s}
            onClick={() => setStatus(v => v === s ? '' : s)}
            className={cn(
              'flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-medium border transition-all',
              statusFilter === s
                ? 'border-[var(--accent)] bg-[var(--accent-muted)] text-[var(--accent)]'
                : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]',
            )}>
            {s.replace('_', ' ')}
            <span className="font-bold">{statusCounts[s] ?? 0}</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-4 flex flex-wrap gap-3">
        <div className="flex items-center gap-1.5 flex-1 min-w-[180px]">
          <Search size={14} className="text-[var(--text-tertiary)] shrink-0"/>
          <input placeholder="Search employee…" value={search} onChange={e => setSearch(e.target.value)}
            className="flex-1 text-[13px] bg-transparent outline-none placeholder:text-[var(--text-tertiary)]"/>
        </div>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          className="h-8 px-2.5 text-[12px] bg-[var(--bg-app)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]"/>
        <select value={deptFilter} onChange={e => setDept(e.target.value)}
          className="h-8 px-2.5 text-[12px] bg-[var(--bg-app)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]">
          <option value="">All Departments</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        {(search || deptFilter || statusFilter) && (
          <button onClick={() => { setSearch(''); setDept(''); setStatus(''); }}
            className="h-8 px-2.5 text-[12px] border border-[var(--border)] rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] flex items-center gap-1.5">
            <X size={11}/> Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-[var(--border)] bg-[var(--bg-app)]">
                {['Employee', 'Code', 'Department', 'Status', 'Check In', 'Check Out', 'Hours', 'Method', 'Late By'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b border-[var(--border)]">
                    {Array.from({ length: 9 }).map((_, j) => (
                      <td key={j} className="px-4 py-3"><div className="h-4 bg-[var(--bg-hover)] rounded animate-pulse w-20"/></td>
                    ))}
                  </tr>
                ))
              ) : records.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-[var(--text-tertiary)]">No records for the selected date/filters</td></tr>
              ) : records.map((r, i) => (
                <tr key={i} className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg-hover)] transition-colors">
                  <td className="px-4 py-3 font-medium text-[var(--text-primary)] whitespace-nowrap">
                    {r.name || `${r.first_name || ''} ${r.last_name || ''}`.trim()}
                  </td>
                  <td className="px-4 py-3 font-mono text-[12px] text-[var(--text-tertiary)]">{r.employee_code}</td>
                  <td className="px-4 py-3 text-[var(--text-secondary)]">{r.department?.name || r.department || '—'}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={r.status}/>
                  </td>
                  <td className="px-4 py-3 tabular-nums text-[var(--text-secondary)]">{r.check_in || '—'}</td>
                  <td className="px-4 py-3 tabular-nums text-[var(--text-secondary)]">{r.check_out || '—'}</td>
                  <td className="px-4 py-3 tabular-nums text-[var(--text-secondary)]">{r.hours_worked ? `${r.hours_worked}h` : '—'}</td>
                  <td className="px-4 py-3 text-[var(--text-tertiary)] text-[12px]">{r.method || '—'}</td>
                  <td className="px-4 py-3 text-[var(--text-secondary)]">
                    {r.late_minutes ? (
                      <span className="text-amber-600 font-medium">{r.late_minutes} min</span>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-[var(--border)] bg-[var(--bg-app)] text-[12px] text-[var(--text-tertiary)]">
          {records.length} records for {date}
        </div>
      </div>
    </div>
  );
}
